import React, { useMemo, useState } from "react";
import styles from "./ChartRangePicker.module.scss";

// One key per pill. "custom" reveals two date inputs.
export type RangeKey = "60d" | "1y" | "all" | "custom";

export interface ChartRange {
  key: RangeKey;
  start: string; // YYYY-MM-DD (UTC)
  end: string; // YYYY-MM-DD (UTC)
}

interface Props {
  // Earliest data point in the chart's series, as YYYY-MM-DD. Used as
  // the start date when "All Time" is selected.
  earliest: string;
  selected: ChartRange;
  onChange: (next: ChartRange) => void;
}

function todayUTC(): string {
  return new Date().toISOString().slice(0, 10);
}

function nDaysAgoUTC(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
}

// Build a Range for a given pill key. Earliest is the floor for the
// "all time" pill; for fixed windows we ignore it and just look back N
// days from today.
export function rangeFor(key: RangeKey, earliest: string): ChartRange {
  const end = todayUTC();
  if (key === "60d") return { key, start: nDaysAgoUTC(60), end };
  if (key === "1y") return { key, start: nDaysAgoUTC(365), end };
  if (key === "all") return { key, start: earliest, end };
  // custom: caller controls — start with all-time bounds as a sensible
  // initial value
  return { key: "custom", start: earliest, end };
}

// Pill-style range selector + custom date inputs. Compact enough to sit
// inline next to a chart title without crowding the card.
const ChartRangePicker: React.FC<Props> = ({
  earliest,
  selected,
  onChange,
}) => {
  const pills: { key: RangeKey; label: string }[] = [
    { key: "60d", label: "60d" },
    { key: "1y", label: "1y" },
    { key: "all", label: "All Time" },
    { key: "custom", label: "Custom" },
  ];
  const onPill = (key: RangeKey) => {
    if (key === "custom") {
      // Preserve the currently-selected range as the starting point
      // so the date inputs don't snap back to defaults each time the
      // user re-clicks Custom.
      onChange({
        key: "custom",
        start: selected.start,
        end: selected.end,
      });
    } else {
      onChange(rangeFor(key, earliest));
    }
  };
  const onStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ key: "custom", start: e.target.value, end: selected.end });
  };
  const onEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ key: "custom", start: selected.start, end: e.target.value });
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.pills}>
        {pills.map((p) => (
          <button
            key={p.key}
            className={`${styles.pill} ${
              selected.key === p.key ? styles.pillActive : ""
            }`}
            onClick={() => onPill(p.key)}
            type="button"
          >
            {p.label}
          </button>
        ))}
      </div>
      {selected.key === "custom" && (
        <div className={styles.customInputs}>
          <input
            type="date"
            value={selected.start}
            min={earliest}
            max={selected.end}
            onChange={onStartChange}
            className={styles.dateInput}
          />
          <span className={styles.dateSep}>→</span>
          <input
            type="date"
            value={selected.end}
            min={selected.start}
            max={todayUTC()}
            onChange={onEndChange}
            className={styles.dateInput}
          />
        </div>
      )}
    </div>
  );
};

export default ChartRangePicker;

// Hook for managing a chart's range state. Stores the pill key + any
// custom-range overrides; recomputes the resolved {start, end} on each
// render based on the current earliest-available date. This means the
// "All Time" pill stays anchored to the actual data start as new data
// flows in (no stale defaults).
export function useChartRange(earliest: string) {
  const [rangeKey, setRangeKey] = useState<RangeKey>("all");
  const [customRange, setCustomRange] = useState<{
    start: string;
    end: string;
  } | null>(null);

  const range = useMemo<ChartRange>(() => {
    if (rangeKey === "custom" && customRange) {
      return { key: "custom", ...customRange };
    }
    return rangeFor(rangeKey, earliest);
  }, [rangeKey, customRange, earliest]);

  const setRange = (next: ChartRange) => {
    setRangeKey(next.key);
    if (next.key === "custom") {
      setCustomRange({ start: next.start, end: next.end });
    }
  };

  return { range, setRange };
}

// Filter an x/y point array to a range (inclusive on both ends).
export function filterByRange<T extends { x: string }>(
  rows: T[],
  range: ChartRange
): T[] {
  return rows.filter((r) => r.x >= range.start && r.x <= range.end);
}
